import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Skeleton } from './skeleton';

interface StatCardProps {
  label: string;
  value: ReactNode;
  /** Signed change; drives colour and arrow direction. */
  delta?: { value: string; direction: 1 | 0 | -1 };
  hint?: ReactNode;
  icon?: LucideIcon;
  loading?: boolean;
  className?: string;
}

export function StatCard({ label, value, delta, hint, icon: Icon, loading, className }: StatCardProps) {
  return (
    <div className={cn('rounded-xl border border-line bg-surface p-4', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium text-fg-subtle">{label}</p>
        {Icon && <Icon className="h-4 w-4 shrink-0 text-fg-faint" aria-hidden="true" />}
      </div>

      {loading ? (
        <Skeleton className="mt-3 h-7 w-28" />
      ) : (
        <p className="mt-2 font-tabular text-xl font-semibold tracking-tight text-fg">{value}</p>
      )}

      {(delta || hint) && !loading && (
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          {delta && (
            <span
              className={cn(
                'inline-flex items-center gap-0.5 font-medium',
                delta.direction > 0 ? 'text-profit' : delta.direction < 0 ? 'text-loss' : 'text-fg-muted'
              )}
            >
              {delta.direction > 0 ? (
                <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
              ) : delta.direction < 0 ? (
                <ArrowDownRight className="h-3 w-3" aria-hidden="true" />
              ) : null}
              {delta.value}
            </span>
          )}
          {hint && <span className="text-fg-faint">{hint}</span>}
        </div>
      )}
    </div>
  );
}
